import { useEffect, useState, useCallback, useRef } from "react";
import { Users, Search, Plus, Filter, MoreVertical, X, MapPin, Phone, Mail, Building2, Tag, NotebookPen, Calendar, Trash2, Archive, ArchiveRestore, Star, Edit3, ArrowUpDown, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";
import { AddressFields, EMPTY_ADDRESS } from "@/components/AddressFields";
import { ContactDialog } from "@/pages/Contacts";
import { TagPicker, TagFilterPopover, TagManagerDialog, BulkTagDialog } from "@/components/contacts/TagSelectors";

/**
 * Clients tab — extends `contacts` with optional CRM-style profile data.
 *
 * Design contract:
 *  • Single source of truth: contacts collection.
 *  • `is_client` is a SOFT flag — toggling it never destroys data.
 *  • Auto-promoted on every public booking submit (backend side).
 *  • Default view: only_clients=true (the "Clients" tab). Filter pills
 *    let host see Active / Inactive / Archived subsets.
 *  • Clicking a row opens ClientProfileDrawer with full bookings/LTV.
 *
 * Backend endpoints:
 *   GET    /booking/clients?workspace_id=&only_clients=&status=&search=
 *   GET    /booking/clients/{id}?workspace_id=
 *   PATCH  /booking/clients/{id}?workspace_id=
 *   POST   /booking/clients/{id}/promote
 *   POST   /booking/clients/{id}/demote
 *   POST   /booking/clients/{id}/archive
 *   POST   /booking/clients/{id}/restore
 *   DELETE /booking/clients/{id}?workspace_id=&hard=
 */
export default function ClientsPanel({ workspace }) {
  const workspaceId = workspace?.workspace_id;
  const [data, setData] = useState({ contacts: [], total: 0, summary: { all_contacts: 0, clients: 0, active: 0, inactive: 0, archived: 0 } });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // Unified filter pill state — collapses the prior two-row toggle
  // (showAll mode + status sub-filter) into a single "view" value that
  // the backend understands directly: active|inactive|archived|clients|contacts|all.
  const [view, setView] = useState("active");
  const [drawerId, setDrawerId] = useState(null);
  // "Add client" / "Add contact" dialog state — shares the same form
  // as the sidebar Contacts page so the schema stays in lockstep.
  const [addOpen, setAddOpen] = useState(false);
  const [editContact, setEditContact] = useState(null);
  // Tag catalog (separate `contact_tags` collection) + the currently-
  // selected filter tags (array of NAMES; AND semantics server-side).
  const [tagCatalog, setTagCatalog] = useState([]);
  const [tagFilter, setTagFilter] = useState([]);
  const [tagManageOpen, setTagManageOpen] = useState(false);
  // Sort options: recently_updated | recently_added | first_asc | first_desc | last_asc | last_desc | email_asc | email_desc | tag_asc | tag_desc | ltv_desc | ltv_asc
  const [sort, setSort] = useState("recently_updated");
  // Multi-select state — set of contact_ids currently checked.
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkTagOpen, setBulkTagOpen] = useState(false);

  // Decode the unified `view` pill into the params the backend expects.
  // `view` values map as follows:
  //   active|inactive|archived → only clients with that client_status
  //   clients                  → all clients (any status)
  //   contacts                 → non-client contacts only
  //   all                      → everyone (clients + contacts)
  const viewParams = (() => {
    if (view === "contacts") return { view: "contacts", status: "all" };
    if (view === "all") return { view: "all", status: "all" };
    if (view === "clients") return { view: "clients", status: "all" };
    return { view: "clients", status: view }; // active|inactive|archived
  })();

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const r = await api.get(`/booking/clients`, {
        params: {
          workspace_id: workspaceId,
          ...viewParams,
          search,
          tags: tagFilter.join(","),
          sort,
        },
      });
      setData(r.data || { contacts: [], total: 0, summary: {} });
    } catch {
      setData({ contacts: [], total: 0, summary: {} });
    } finally {
      setLoading(false);
    }
    // viewParams is recomputed on every render — depend on its source instead
    // so we don't loop. eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, view, search, tagFilter, sort]);

  const loadTagCatalog = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const r = await api.get(`/booking/tags`, { params: { workspace_id: workspaceId } });
      setTagCatalog(r.data || []);
    } catch { setTagCatalog([]); }
  }, [workspaceId]);

  // Debounce search by 250ms so we don't hammer the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => { loadTagCatalog(); }, [loadTagCatalog]);

  // Clear bulk selection whenever the visible result set is changed by
  // a filter/search/sort/view tweak — keeps the toolbar in sync with
  // the rows the user can actually see.
  useEffect(() => { setSelected(new Set()); }, [view, search, sort, tagFilter]);

  const { summary } = data;

  return (
    <div className="space-y-5" data-testid="clients-panel">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#005e7a] text-white flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5" />
          </div>
          <div>
            <h2 className="bloq-headline text-2xl">Clients</h2>
            <p className="text-sm text-[#4a4a52]">
              People you've worked with. Auto-promoted on every booking — toggle the filter off to see plain contacts too.
            </p>
          </div>
        </div>
        <button
          onClick={() => { setEditContact(null); setAddOpen(true); }}
          className="bloq-btn-primary inline-flex items-center gap-2 text-sm"
          data-testid="add-client-button"
        >
          <Plus className="w-4 h-4" /> Add client
        </button>
      </div>

      <ContactDialog
        open={addOpen}
        onOpenChange={(o) => { setAddOpen(o); if (!o) setEditContact(null); }}
        contact={editContact}
        onSaved={async (saved) => {
          setAddOpen(false);
          // If this is a brand-new contact (no editContact), auto-promote
          // it to client status so it lands in the Clients view immediately.
          if (!editContact && saved?.contact_id && workspaceId) {
            try {
              await api.post(`/booking/clients/${saved.contact_id}/promote`, null, { params: { workspace_id: workspaceId } });
            } catch { /* non-fatal */ }
          }
          setEditContact(null);
          load();
        }}
      />

      {/* ── Toolbar ────────────────────────────────────────────────── */}
      <div className="bloq-card p-4 space-y-3" data-testid="clients-toolbar">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#82828c]" />
            <input
              type="search"
              placeholder="Search by name, email, phone, company…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bloq-input pl-10 w-full"
              data-testid="clients-search"
            />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-44" data-testid="clients-sort">
              <ArrowUpDown className="w-3.5 h-3.5 mr-1.5 text-[#82828c]" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recently_updated">Recently updated</SelectItem>
              <SelectItem value="recently_added">Recently added</SelectItem>
              <SelectItem value="first_asc">First name A–Z</SelectItem>
              <SelectItem value="first_desc">First name Z–A</SelectItem>
              <SelectItem value="last_asc">Last name A–Z</SelectItem>
              <SelectItem value="last_desc">Last name Z–A</SelectItem>
              <SelectItem value="email_asc">Email A–Z</SelectItem>
              <SelectItem value="email_desc">Email Z–A</SelectItem>
              <SelectItem value="tag_asc">Tag A–Z</SelectItem>
              <SelectItem value="tag_desc">Tag Z–A</SelectItem>
              <SelectItem value="ltv_desc">Lifetime value (high → low)</SelectItem>
              <SelectItem value="ltv_asc">Lifetime value (low → high)</SelectItem>
            </SelectContent>
          </Select>
          <TagFilterPopover
            catalog={tagCatalog}
            value={tagFilter}
            onChange={setTagFilter}
            onManage={() => setTagManageOpen(true)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Unified filter pills — in the order requested by product:
              Active, Inactive, Archived, Clients, Contacts, All. The
              first four narrow to client subsets; "Contacts" shows
              non-clients only; "All" shows everyone. */}
          <FilterPill v={view} setV={setView} k="active"   label={`Active (${summary?.active || 0})`}       testid="clients-status-active" />
          <FilterPill v={view} setV={setView} k="inactive" label={`Inactive (${summary?.inactive || 0})`}   testid="clients-status-inactive" />
          <FilterPill v={view} setV={setView} k="archived" label={`Archived (${summary?.archived || 0})`}   testid="clients-status-archived" />
          <FilterPill v={view} setV={setView} k="clients"  label={`Clients (${summary?.clients || 0})`}     testid="clients-filter-clients" />
          <FilterPill v={view} setV={setView} k="contacts" label={`Contacts (${summary?.non_clients || 0})`} testid="clients-filter-contacts" />
          <FilterPill v={view} setV={setView} k="all"      label={`All (${summary?.all_contacts || 0})`}    testid="clients-filter-all" />
        </div>
      </div>

      {/* ── Bulk-actions bar ───────────────────────────────────────── */}
      {selected.size > 0 && (
        <div
          className="bloq-card p-3 flex flex-wrap items-center gap-2 border border-[#005e7a]/30 bg-[#005e7a]/5"
          data-testid="clients-bulk-bar"
        >
          <span className="text-xs font-medium text-[#1a1a1f]" data-testid="clients-bulk-count">
            {selected.size} selected
          </span>
          <button
            onClick={() => setSelected(new Set())}
            className="text-xs text-[#82828c] hover:text-[#1a1a1f] underline"
            data-testid="clients-bulk-clear"
          >
            Clear
          </button>
          <span className="flex-1" />
          <button
            disabled={bulkBusy}
            onClick={() => setBulkTagOpen(true)}
            className="bloq-btn-secondary text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="clients-bulk-tag"
          >
            <Tag className="w-3.5 h-3.5" /> Edit tags
          </button>
          <button
            disabled={bulkBusy}
            onClick={async () => {
              setBulkBusy(true);
              try {
                const r = await api.post(`/booking/clients/bulk/status`,
                  { contact_ids: Array.from(selected), status: "active" },
                  { params: { workspace_id: workspaceId } });
                toast.success(`Marked ${r.data?.affected || 0} active`);
                setSelected(new Set());
                load();
              } catch { toast.error("Could not bulk-update"); }
              finally { setBulkBusy(false); }
            }}
            className="bloq-btn-secondary text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="clients-bulk-active"
          >
            <Star className="w-3.5 h-3.5" /> Active
          </button>
          <button
            disabled={bulkBusy}
            onClick={async () => {
              setBulkBusy(true);
              try {
                const r = await api.post(`/booking/clients/bulk/status`,
                  { contact_ids: Array.from(selected), status: "inactive" },
                  { params: { workspace_id: workspaceId } });
                toast.success(`Marked ${r.data?.affected || 0} inactive`);
                setSelected(new Set());
                load();
              } catch { toast.error("Could not bulk-update"); }
              finally { setBulkBusy(false); }
            }}
            className="bloq-btn-secondary text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="clients-bulk-inactive"
          >
            <Star className="w-3.5 h-3.5" /> Inactive
          </button>
          <button
            disabled={bulkBusy}
            onClick={async () => {
              setBulkBusy(true);
              try {
                const r = await api.post(`/booking/clients/bulk/status`,
                  { contact_ids: Array.from(selected), status: "archived" },
                  { params: { workspace_id: workspaceId } });
                toast.success(`Archived ${r.data?.affected || 0}`);
                setSelected(new Set());
                load();
              } catch { toast.error("Could not bulk-archive"); }
              finally { setBulkBusy(false); }
            }}
            className="bloq-btn-secondary text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="clients-bulk-archive"
          >
            <Archive className="w-3.5 h-3.5" /> Archive
          </button>
          <button
            disabled={bulkBusy}
            onClick={async () => {
              const n = selected.size;
              if (!window.confirm(`Delete ${n} contact${n === 1 ? "" : "s"}? This cannot be undone.`)) return;
              setBulkBusy(true);
              try {
                const r = await api.post(`/booking/clients/bulk/delete`,
                  { contact_ids: Array.from(selected) },
                  { params: { workspace_id: workspaceId, hard: true } });
                toast.success(`Deleted ${r.data?.affected || 0}`);
                setSelected(new Set());
                load();
              } catch { toast.error("Could not bulk-delete"); }
              finally { setBulkBusy(false); }
            }}
            className="text-xs px-3 py-1.5 bg-[#c43838] text-white hover:bg-[#a82c2c] transition-colors inline-flex items-center gap-1.5 disabled:opacity-60"
            style={{ borderRadius: 9999 }}
            data-testid="clients-bulk-delete"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      )}

      {/* ── List ───────────────────────────────────────────────────── */}
      <div className="bloq-card p-0 overflow-hidden" data-testid="clients-list-wrap">
        {loading ? (
          <p className="text-center text-sm text-[#82828c] py-10">Loading…</p>
        ) : data.contacts.length === 0 ? (
          <EmptyClientsState view={view} hasSearch={!!search.trim()} />
        ) : (
          <table className="w-full text-sm" data-testid="clients-table">
            <thead className="text-[10px] uppercase tracking-wider text-[#82828c] border-b border-[#e3dccc] bg-[#fafaf3]">
              <tr>
                <th className="py-2.5 px-3 w-8">
                  <input
                    type="checkbox"
                    className="w-3.5 h-3.5 accent-[#005e7a] cursor-pointer"
                    checked={data.contacts.length > 0 && data.contacts.every((c) => selected.has(c.contact_id))}
                    ref={(el) => {
                      if (!el) return;
                      const some = data.contacts.some((c) => selected.has(c.contact_id));
                      const all = data.contacts.every((c) => selected.has(c.contact_id));
                      el.indeterminate = some && !all;
                    }}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelected(new Set([...selected, ...data.contacts.map((c) => c.contact_id)]));
                      } else {
                        const next = new Set(selected);
                        data.contacts.forEach((c) => next.delete(c.contact_id));
                        setSelected(next);
                      }
                    }}
                    data-testid="clients-select-all"
                    aria-label="Select all visible"
                  />
                </th>
                <th className="text-left py-2.5 px-4">Name</th>
                <th className="text-left py-2.5 px-4">Contact</th>
                <th className="text-left py-2.5 px-4">Company</th>
                <th className="text-left py-2.5 px-4">Status</th>
                <th className="text-right py-2.5 px-4">Bookings</th>
                <th className="text-right py-2.5 px-4">Lifetime value</th>
                <th className="text-right py-2.5 px-4">Last booking</th>
                <th className="py-2.5 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.contacts.map((c) => (
                <ClientRow
                  key={c.contact_id}
                  contact={c}
                  workspaceId={workspaceId}
                  selected={selected.has(c.contact_id)}
                  onToggleSelect={() => {
                    const next = new Set(selected);
                    if (next.has(c.contact_id)) next.delete(c.contact_id);
                    else next.add(c.contact_id);
                    setSelected(next);
                  }}
                  onClick={() => setDrawerId(c.contact_id)}
                  onAction={load}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {drawerId && (
        <ClientProfileDrawer
          contactId={drawerId}
          workspaceId={workspaceId}
          tagCatalog={tagCatalog}
          onClose={() => setDrawerId(null)}
          onChanged={load}
        />
      )}

      <TagManagerDialog
        open={tagManageOpen}
        onClose={() => { setTagManageOpen(false); loadTagCatalog(); load(); }}
        workspaceId={workspaceId}
      />

      <BulkTagDialog
        open={bulkTagOpen}
        onClose={() => setBulkTagOpen(false)}
        workspaceId={workspaceId}
        contactIds={Array.from(selected)}
        catalog={tagCatalog}
        onApplied={() => { setSelected(new Set()); load(); }}
      />
    </div>
  );
}

function FilterPill({ v, setV, k, label, testid }) {
  const active = v === k;
  return (
    <button
      type="button"
      onClick={() => setV(k)}
      className={`text-xs px-3 py-1.5 transition-colors ${active ? "bg-[#005e7a] text-white" : "bg-[#f5efe6] text-[#4a4a52] hover:bg-[#ebe3d1]"}`}
      style={{ borderRadius: 9999 }}
      data-testid={testid}
    >
      {label}
    </button>
  );
}

function EmptyClientsState({ view, hasSearch }) {
  return (
    <div className="text-center py-12 px-6 text-sm text-[#82828c]" data-testid="clients-empty">
      {hasSearch ? (
        <p>No contacts match that search.</p>
      ) : view === "all" || view === "contacts" ? (
        <>
          <p className="font-medium text-[#4a4a52] mb-1">No contacts yet</p>
          <p className="text-xs">Once you take your first booking, the invitee shows up here.</p>
        </>
      ) : (
        <>
          <p className="font-medium text-[#4a4a52] mb-1">No clients yet</p>
          <p className="text-xs">Contacts auto-promote to clients on their first booking. Switch to &ldquo;Contacts&rdquo; or &ldquo;All&rdquo; to see everyone.</p>
        </>
      )}
    </div>
  );
}

function ClientRow({ contact, workspaceId, onClick, onAction, selected = false, onToggleSelect }) {
  const ltv = ((contact.lifetime_value_minor || 0) / 100);
  const ltvLabel = ltv ? `$${ltv.toLocaleString(undefined, { minimumFractionDigits: ltv === Math.floor(ltv) ? 0 : 2 })}` : "—";
  const lastBooking = contact.last_booking_at ? new Date(contact.last_booking_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";
  const fullName = contact.name || `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || contact.email || "—";

  const promote = async (e) => { e.stopPropagation();
    try { await api.post(`/booking/clients/${contact.contact_id}/promote`, null, { params: { workspace_id: workspaceId } }); toast.success("Promoted to client"); onAction(); }
    catch { toast.error("Could not promote"); }
  };
  const demote = async (e) => { e.stopPropagation();
    try { await api.post(`/booking/clients/${contact.contact_id}/demote`, null, { params: { workspace_id: workspaceId } }); toast.success("Client flag turned off"); onAction(); }
    catch { toast.error("Could not demote"); }
  };
  const setStatus = async (e, newStatus) => { e.stopPropagation();
    const route = newStatus === "archived" ? "archive" : "restore";
    try { await api.post(`/booking/clients/${contact.contact_id}/${route}`, null, { params: { workspace_id: workspaceId } }); toast.success(`Marked ${newStatus}`); onAction(); }
    catch { toast.error("Could not update status"); }
  };
  const softDelete = async (e) => { e.stopPropagation();
    if (!window.confirm(`Delete ${fullName}? They can be restored from Archived view.`)) return;
    try { await api.delete(`/booking/clients/${contact.contact_id}`, { params: { workspace_id: workspaceId } }); toast.success("Deleted"); onAction(); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <tr
      onClick={onClick}
      className={`border-b border-[#f0eadc] hover:bg-[#fafaf3] cursor-pointer ${selected ? "bg-[#005e7a]/5" : ""}`}
      data-testid={`client-row-${contact.contact_id}`}
    >
      <td className="py-3 px-3 w-8" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          className="w-3.5 h-3.5 accent-[#005e7a] cursor-pointer"
          checked={selected}
          onChange={onToggleSelect}
          data-testid={`client-select-${contact.contact_id}`}
          aria-label={`Select ${fullName}`}
        />
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-[#005e7a]/10 text-[#005e7a] flex items-center justify-center flex-shrink-0 font-semibold text-xs">
            {(fullName.charAt(0) || "?").toUpperCase()}
          </div>
          <div>
            <p className="font-medium text-[#1a1a1f]">{fullName}</p>
            {contact.is_client && (
              <p className="text-[10px] text-[#005e7a] uppercase tracking-wider font-semibold mt-0.5">Client</p>
            )}
          </div>
        </div>
      </td>
      <td className="py-3 px-4 text-xs text-[#4a4a52]">
        {contact.email && <p>{contact.email}</p>}
        {contact.phone && <p className="text-[#82828c] mt-0.5">{contact.phone}</p>}
      </td>
      <td className="py-3 px-4 text-xs text-[#4a4a52]">
        {contact.company || <span className="text-[#82828c]">—</span>}
      </td>
      <td className="py-3 px-4">
        {contact.is_client ? (
          <StatusBadge status={contact.client_status || "active"} />
        ) : (
          <span className="text-[10px] uppercase tracking-wider text-[#82828c]">Contact</span>
        )}
      </td>
      <td className="py-3 px-4 text-right text-sm font-medium">{contact.total_bookings || 0}</td>
      <td className="py-3 px-4 text-right text-sm font-medium">{ltvLabel}</td>
      <td className="py-3 px-4 text-right text-xs text-[#82828c]">{lastBooking}</td>
      <td className="py-3 px-2 text-right" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-1.5 rounded hover:bg-[#f5efe6]" data-testid={`client-actions-${contact.contact_id}`}>
              <MoreVertical className="w-4 h-4 text-[#82828c]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            {contact.is_client ? (
              <DropdownMenuItem onClick={demote}><Star className="w-3.5 h-3.5 mr-2" /> Remove client flag</DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={promote}><Star className="w-3.5 h-3.5 mr-2" /> Mark as client</DropdownMenuItem>
            )}
            {contact.is_client && contact.client_status !== "archived" && (
              <DropdownMenuItem onClick={(e) => setStatus(e, "archived")}><Archive className="w-3.5 h-3.5 mr-2" /> Archive</DropdownMenuItem>
            )}
            {contact.is_client && contact.client_status === "archived" && (
              <DropdownMenuItem onClick={(e) => setStatus(e, "active")}><ArchiveRestore className="w-3.5 h-3.5 mr-2" /> Restore</DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={softDelete} className="text-red-600 focus:text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" /> Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.active;
  return (
    <span className="bloq-pill text-[10px] uppercase tracking-wider font-semibold" style={{ background: s.bg, color: s.color }}>
      {status}
    </span>
  );
}

const STATUS_STYLE = {
  active:   { bg: "rgba(34,197,94,0.15)",  color: "#15803d" },
  inactive: { bg: "rgba(180,83,9,0.15)",   color: "#b45309" },
  archived: { bg: "rgba(130,130,140,0.18)",color: "#4a4a52" },
};

// ---------------------------------------------------------------------------
// Drawer — full profile + bookings + editable address/tags/notes
// ---------------------------------------------------------------------------
function ClientProfileDrawer({ contactId, workspaceId, tagCatalog, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/booking/clients/${contactId}`, { params: { workspace_id: workspaceId } });
      setData(r.data);
      const c = r.data?.contact || {};
      setForm({
        first_name: c.first_name || "",
        last_name: c.last_name || "",
        phone: c.phone || "",
        company: c.company || "",
        pronouns: c.pronouns || "",
        birthday: c.birthday || "",
        tags: c.tags || [],
        internal_notes: c.internal_notes || "",
        client_since: c.client_since ? c.client_since.slice(0, 10) : "",
        client_status: c.client_status || "active",
        address: c.address && (c.address.line1 || c.address.city)
          ? { ...EMPTY_ADDRESS, ...c.address }
          : { ...EMPTY_ADDRESS },
      });
    } catch {
      toast.error("Could not load profile");
    } finally {
      setLoading(false);
    }
  }, [contactId, workspaceId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        company: form.company.trim(),
        pronouns: form.pronouns.trim(),
        birthday: form.birthday || null,
        tags: form.tags,
        internal_notes: form.internal_notes,
        address: form.address,
        client_since: form.client_since
          ? new Date(`${form.client_since}T00:00:00Z`).toISOString()
          : null,
        client_status: form.client_status,
      };
      await api.patch(`/booking/clients/${contactId}`, payload, { params: { workspace_id: workspaceId } });
      toast.success("Profile saved");
      setEditing(false);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const flipClient = async (turnOn) => {
    try {
      await api.post(`/booking/clients/${contactId}/${turnOn ? "promote" : "demote"}`, null, { params: { workspace_id: workspaceId } });
      toast.success(turnOn ? "Marked as client" : "Client flag removed");
      load();
      onChanged?.();
    } catch {
      toast.error("Could not update flag");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="client-profile-drawer">
        {loading || !data ? (
          <p className="text-center text-sm text-[#82828c] py-10">Loading…</p>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-12 h-12 rounded-full bg-[#005e7a]/10 text-[#005e7a] flex items-center justify-center font-semibold text-lg flex-shrink-0">
                    {((data.contact?.first_name || data.contact?.name || "?").charAt(0)).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <DialogTitle className="text-2xl">{data.contact?.name || `${data.contact?.first_name || ""} ${data.contact?.last_name || ""}`.trim() || "—"}</DialogTitle>
                    <div className="flex items-center gap-2 mt-1">
                      {data.contact?.is_client ? <StatusBadge status={data.contact?.client_status || "active"} /> : <span className="text-[10px] uppercase tracking-wider text-[#82828c]">Contact</span>}
                      {data.contact?.client_since && (
                        <span className="text-[11px] text-[#82828c]">· Client since {new Date(data.contact.client_since).toLocaleDateString(undefined, { month: "short", year: "numeric" })}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => flipClient(!data.contact?.is_client)}
                    className="bloq-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
                    data-testid="drawer-flip-client"
                  >
                    <Star className="w-3.5 h-3.5" />
                    {data.contact?.is_client ? "Remove client flag" : "Mark as client"}
                  </button>
                  {!editing ? (
                    <button onClick={() => setEditing(true)} className="bloq-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1.5" data-testid="drawer-edit">
                      <Edit3 className="w-3.5 h-3.5" /> Edit
                    </button>
                  ) : (
                    <>
                      <button onClick={() => { setEditing(false); load(); }} className="bloq-btn-secondary text-xs px-3 py-1.5">Cancel</button>
                      <button onClick={save} className="bloq-btn-primary text-xs px-3 py-1.5" data-testid="drawer-save">Save</button>
                    </>
                  )}
                </div>
              </div>
            </DialogHeader>

            {/* KPIs */}
            <div className="grid grid-cols-3 gap-3 mt-4">
              <Kpi label="Total bookings" value={data.contact?.total_bookings || 0} icon={<Calendar className="w-3.5 h-3.5" />} />
              <Kpi label="Lifetime value" value={`$${(((data.contact?.lifetime_value_minor || 0) / 100)).toLocaleString(undefined, { minimumFractionDigits: 0 })}`} icon={<Star className="w-3.5 h-3.5" />} />
              <Kpi label="Last booking" value={data.contact?.last_booking_at ? new Date(data.contact.last_booking_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"} icon={<Calendar className="w-3.5 h-3.5" />} />
            </div>

            {/* Contact */}
            <Section title="Contact details" icon={<Mail className="w-4 h-4" />}>
              {editing ? (
                <div className="grid grid-cols-2 gap-3">
                  <FieldBox label="First name"><input className="bloq-input" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></FieldBox>
                  <FieldBox label="Last name"><input className="bloq-input" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></FieldBox>
                  <FieldBox label="Email (read-only)"><input className="bloq-input" value={data.contact?.email || ""} disabled /></FieldBox>
                  <FieldBox label="Phone"><input className="bloq-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></FieldBox>
                  <FieldBox label="Company"><input className="bloq-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></FieldBox>
                  <FieldBox label="Birthday"><input className="bloq-input" type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} /></FieldBox>
                  <PronounsField value={form.pronouns} onChange={(v) => setForm({ ...form, pronouns: v })} />
                </div>
              ) : (
                <Definition>
                  <Detail label="Email" value={data.contact?.email} icon={<Mail className="w-3.5 h-3.5" />} />
                  <Detail label="Phone" value={data.contact?.phone} icon={<Phone className="w-3.5 h-3.5" />} />
                  <Detail label="Company" value={data.contact?.company} icon={<Building2 className="w-3.5 h-3.5" />} />
                  {data.contact?.pronouns && <Detail label="Pronouns" value={data.contact?.pronouns} />}
                  {data.contact?.birthday && <Detail label="Birthday" value={data.contact?.birthday} />}
                </Definition>
              )}
            </Section>

            {/* Client status / start date */}
            {data.contact?.is_client && (
              <Section title="Client" icon={<Star className="w-4 h-4" />}>
                {editing ? (
                  <div className="grid grid-cols-2 gap-3">
                    <FieldBox label="Status">
                      <Select value={form.client_status} onValueChange={(v) => setForm({ ...form, client_status: v })}>
                        <SelectTrigger data-testid="client-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                          <SelectItem value="archived">Archived</SelectItem>
                        </SelectContent>
                      </Select>
                    </FieldBox>
                    <FieldBox label="Client start date">
                      <input
                        className="bloq-input"
                        type="date"
                        value={form.client_since || ""}
                        onChange={(e) => setForm({ ...form, client_since: e.target.value })}
                        data-testid="client-since-field"
                      />
                    </FieldBox>
                  </div>
                ) : (
                  <Definition>
                    <Detail label="Status" value={(data.contact?.client_status || "active").charAt(0).toUpperCase() + (data.contact?.client_status || "active").slice(1)} />
                    {data.contact?.client_since && (
                      <Detail label="Client since" value={new Date(data.contact.client_since).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })} />
                    )}
                  </Definition>
                )}
              </Section>
            )}

            {/* Address */}
            <Section title="Address" icon={<MapPin className="w-4 h-4" />}>
              {editing ? (
                <AddressFields
                  value={form.address}
                  onChange={(addr) => setForm({ ...form, address: addr })}
                />
              ) : (
                <AddressView a={data.contact?.address} />
              )}
            </Section>

            {/* Tags + Notes */}
            <Section title="Tags & notes" icon={<Tag className="w-4 h-4" />}>
              {editing ? (
                <>
                  <FieldBox label="Tags" full>
                    <TagPicker
                      catalog={tagCatalog}
                      value={form.tags}
                      onChange={(tags) => setForm({ ...form, tags })}
                    />
                    {tagCatalog.length === 0 && (
                      <p className="text-[11px] text-[#82828c] mt-1.5">
                        No tags yet — create some via <strong>Manage tags</strong> in the toolbar.
                      </p>
                    )}
                  </FieldBox>
                  <FieldBox label="Internal notes (host-only)" full>
                    <textarea rows={4} className="bloq-input" value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} />
                  </FieldBox>
                </>
              ) : (
                <>
                  {(data.contact?.tags || []).length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {data.contact.tags.map((tName) => {
                        const tag = (tagCatalog || []).find((t) => t.name === tName);
                        const color = tag?.color || "#005e7a";
                        return (
                          <span
                            key={tName}
                            className="text-[10px] font-medium uppercase tracking-wider px-2 py-1 leading-none"
                            style={{ borderRadius: 9999, background: `${color}22`, color }}
                          >
                            {tName}
                          </span>
                        );
                      })}
                    </div>
                  ) : <p className="text-xs text-[#82828c] mb-2">No tags yet.</p>}
                  {data.contact?.internal_notes ? (
                    <div className="bg-[#fafaf3] p-3 rounded-md text-sm text-[#4a4a52] whitespace-pre-wrap">{data.contact.internal_notes}</div>
                  ) : <p className="text-xs text-[#82828c]">No internal notes.</p>}
                </>
              )}
            </Section>

            {/* Booking history */}
            <Section title={`Booking history (${(data.bookings || []).length})`} icon={<Calendar className="w-4 h-4" />}>
              {(data.bookings || []).length === 0 ? (
                <p className="text-xs text-[#82828c]">No bookings yet.</p>
              ) : (
                <table className="w-full text-xs" data-testid="drawer-bookings-table">
                  <thead className="text-[10px] uppercase tracking-wider text-[#82828c] border-b border-[#e3dccc]">
                    <tr>
                      <th className="text-left py-2 pr-3">Appointment</th>
                      <th className="text-left py-2 pr-3">Date</th>
                      <th className="text-left py-2 pr-3">Status</th>
                      <th className="text-left py-2 pr-3">Payment</th>
                      <th className="text-right py-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.bookings.map((b) => (
                      <tr key={b.booking_id} className="border-b border-[#f0eadc]">
                        <td className="py-2 pr-3">{b.appointment_type_name || "—"}</td>
                        <td className="py-2 pr-3 text-[#82828c]">{b.start_time_utc ? new Date(b.start_time_utc).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
                        <td className="py-2 pr-3"><BookingStatusPill v={b.status} /></td>
                        <td className="py-2 pr-3"><PaymentStatusPill v={b.payment_status} method={b.payment_method} /></td>
                        <td className="py-2 text-right font-medium">${(((b.stripe_payment_amount_minor || 0) / 100)).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>

            <DialogFooter className="mt-4">
              <button onClick={onClose} className="bloq-btn-secondary text-sm" data-testid="drawer-close">Close</button>
              {editing && (
                <button onClick={save} className="bloq-btn-primary text-sm" data-testid="drawer-save-footer">
                  Save changes
                </button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="mt-5">
      <p className="text-[10px] uppercase tracking-widest text-[#82828c] font-semibold mb-2 flex items-center gap-1.5">
        <span className="text-[#005e7a]">{icon}</span> {title}
      </p>
      {children}
    </div>
  );
}

function Definition({ children }) {
  return <dl className="grid grid-cols-2 gap-x-5 gap-y-2 text-sm">{children}</dl>;
}

function Detail({ label, value, icon }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-[#82828c] flex items-center gap-1">
        {icon && <span>{icon}</span>}{label}
      </dt>
      <dd className="text-[#1a1a1f] mt-0.5">{value}</dd>
    </div>
  );
}

function FieldBox({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="text-xs uppercase tracking-wider text-[#82828c] block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

// Pronouns dropdown — mirrors the Contacts dialog so the same options
// appear everywhere. "Other" reveals an inline custom text field.
const PRONOUN_OPTIONS = ["she/her", "he/him", "they/them", "she/they", "he/they", "ze/zir"];

function PronounsField({ value, onChange }) {
  const initialMode = !value ? "none" : (PRONOUN_OPTIONS.includes((value || "").toLowerCase()) ? value.toLowerCase() : "other");
  const [mode, setMode] = useState(initialMode);
  const [custom, setCustom] = useState(mode === "other" ? (value || "") : "");
  useEffect(() => {
    if (mode === "none") onChange("");
    else if (mode === "other") onChange(custom);
    else onChange(mode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, custom]);
  return (
    <>
      <FieldBox label="Pronouns">
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger data-testid="drawer-pronouns-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— Not specified —</SelectItem>
            {PRONOUN_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            <SelectItem value="other">Other (fill in)</SelectItem>
          </SelectContent>
        </Select>
      </FieldBox>
      {mode === "other" && (
        <FieldBox label="Custom pronouns">
          <input className="bloq-input" placeholder="xe/xem" value={custom} onChange={(e) => setCustom(e.target.value)} data-testid="drawer-pronouns-custom" />
        </FieldBox>
      )}
    </>
  );
}

function AddressView({ a }) {
  if (!a || (!a.line1 && !a.city)) return <p className="text-xs text-[#82828c]">No address on file.</p>;
  const cityLine = [a.city, a.state || a.region].filter(Boolean).join(", ");
  return (
    <address className="not-italic text-sm text-[#1a1a1f] leading-relaxed">
      {a.line1 && <p>{a.line1}</p>}
      {a.line2 && <p>{a.line2}</p>}
      {(cityLine || a.postal_code) && <p>{cityLine}{a.postal_code ? ` ${a.postal_code}` : ""}</p>}
      {a.country && <p>{a.country}</p>}
    </address>
  );
}

function Kpi({ label, value, icon }) {
  return (
    <div className="bg-[#f5efe6] p-3 rounded-lg">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#82828c]">
        <span className="text-[#005e7a]">{icon}</span> {label}
      </div>
      <p className="text-lg font-bold text-[#1a1a1f] mt-1">{value}</p>
    </div>
  );
}

function BookingStatusPill({ v }) {
  const map = {
    confirmed: { bg: "rgba(34,197,94,0.15)", color: "#15803d" },
    pending:   { bg: "rgba(180,83,9,0.15)",  color: "#b45309" },
    canceled:  { bg: "rgba(220,38,38,0.15)", color: "#b91c1c" },
    completed: { bg: "rgba(34,197,94,0.10)", color: "#005e7a" },
  };
  const s = map[v] || { bg: "rgba(130,130,140,0.15)", color: "#4a4a52" };
  return <span className="bloq-pill text-[10px] uppercase tracking-wider font-semibold" style={{ background: s.bg, color: s.color }}>{v}</span>;
}

function PaymentStatusPill({ v, method }) {
  if (v === "not_required" || !v) return <span className="text-[#82828c]">—</span>;
  const map = {
    paid:             { bg: "rgba(34,197,94,0.15)", color: "#15803d" },
    awaiting_payment: { bg: "rgba(180,83,9,0.15)",  color: "#b45309" },
    failed:           { bg: "rgba(220,38,38,0.15)", color: "#b91c1c" },
    refunded:         { bg: "rgba(130,130,140,0.18)",color: "#4a4a52" },
  };
  const s = map[v] || { bg: "rgba(130,130,140,0.15)", color: "#4a4a52" };
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <span className="bloq-pill text-[10px] uppercase tracking-wider font-semibold" style={{ background: s.bg, color: s.color }}>{v.replace("_", " ")}</span>
      {method && <span className="text-[10px] text-[#82828c]">{method}</span>}
    </span>
  );
}

